import { addDoc, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import {
    distributionCenterInventoriesRef,
    DistributionCenterInventory
} from '@api/DistributionCenterInventories/DistributionCenterInventory';
import InputServ from '@api/Inputs/InputServ';
import { Input } from '@api/Inputs/Input';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';

export default {
    async saveInventory(inventory: DistributionCenterInventory): Promise<DistributionCenterInventory> {
        if (inventory.input.id) {
            const existInventory = await this.getInventoryByInput(inventory.distributionCenterId, inventory.input.id);
            if (!existInventory) {
                const data = await addDoc(distributionCenterInventoriesRef, inventory);
                inventory.id = data.id;
                await updateDoc(doc(distributionCenterInventoriesRef, inventory.id), { ...inventory });
            } else {
                await updateDoc(doc(distributionCenterInventoriesRef, existInventory.id), {
                    input: inventory.input,
                    stock: existInventory.stock + inventory.stock
                });
            }
        }
        const action = `registro ${inventory.stock} entradas del insumo`;
        await ActivityLogServ.addActivityLog('input', inventory.input.id!, action);
        return inventory;
    },
    async getInventoryByInput(
        distributionCenterId: string,
        inputId: string
    ): Promise<DistributionCenterInventory | undefined> {
        let inventory = undefined;
        const queryDoc = query(
            distributionCenterInventoriesRef,
            where('distributionCenterId', '==', distributionCenterId),
            where('input.id', '==', inputId)
        );
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return inventory;
        }
        snapshot.forEach((doc: any) => {
            inventory = doc.data() as DistributionCenterInventory;
            inventory.id = doc.id;
        });
        return inventory;
    },
    async getInventory(distributionCenterId: string, fillEmpty?: boolean): Promise<DistributionCenterInventory[]> {
        const inventory: DistributionCenterInventory[] = [];
        const queryDoc = query(
            distributionCenterInventoriesRef,
            where('distributionCenterId', '==', distributionCenterId)
        );
        const snapshot = await getDocs(queryDoc);
        if (!snapshot.empty) {
            snapshot.docs.map((doc: any) => {
                inventory.push(doc.data() as DistributionCenterInventory);
            });
        }
        if (fillEmpty) {
            const inputs = await InputServ.getInputs();
            inputs.forEach((input) => {
                // Check if the input is available in this specific distribution center
                const isAvailableInAll = input.distributionCentersAvailable?.includes('all') ?? true;
                const isAvailableInThisCenter =
                    isAvailableInAll || input.distributionCentersAvailable?.includes(distributionCenterId);

                if (isAvailableInThisCenter && input.id) {
                    inventory.push({
                        id: input.id,
                        distributionCenterId,
                        input,
                        stock: 0
                    });
                }
            });
        }
        return inventory.filter((v, i, a) => a.findIndex((v2) => v2.input.id === v.input.id) === i);
    },
    async getInventoriesByInputId(inputId: string): Promise<DistributionCenterInventory[]> {
        const inventories: DistributionCenterInventory[] = [];
        const queryDoc = query(distributionCenterInventoriesRef, where('input.id', '==', inputId));
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return inventories;
        }
        snapshot.forEach((doc: any) => {
            const inventory = doc.data() as DistributionCenterInventory;
            inventory.id = doc.id;
            inventories.push(inventory);
        });
        return inventories;
    },
    async getAllInventories(): Promise<DistributionCenterInventory[]> {
        const inventories: DistributionCenterInventory[] = [];
        const queryDoc = query(distributionCenterInventoriesRef);
        const snapshot = await getDocs(queryDoc);
        if (!snapshot.empty) {
            snapshot.forEach((doc: any) => {
                inventories.push(doc.data() as DistributionCenterInventory);
            });
        }
        return inventories;
    },
    async deleteInventory(inventory: DistributionCenterInventory): Promise<void> {
        // Check if exist
        if (inventory.input.id) {
            const existInventory = await this.getInventoryByInput(inventory.distributionCenterId, inventory.input.id);
            if (existInventory) {
                const newStock = existInventory.stock - inventory.stock;
                if (newStock === 0) {
                    await deleteDoc(doc(distributionCenterInventoriesRef, existInventory.id));
                } else {
                    await updateDoc(doc(distributionCenterInventoriesRef, existInventory.id), {
                        stock: newStock
                    });
                }
            }
        }
        const action = `registro ${inventory.stock} salidas del insumo`;
        await ActivityLogServ.addActivityLog('input', inventory.input.id!, action);
        return;
    },
    async updateInputInventory(inventoryId: string, input: Input, stock: number): Promise<void> {
        await updateDoc(doc(distributionCenterInventoriesRef, inventoryId), { input, stock });
        return;
    }
};
